import { Injectable } from '@angular/core';
import { IProduct, PaymentTerm, PaymentTermService } from '@cemex-core/cmx-api';
import { IJobsiteSalesArea } from '@cemex-core/cmx-api/models/jobsite-sales-areas/jobsite-sales-area';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { filter, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { RESET_BUILDER } from '../../../../actions/order-builder.actions';
import { getJobsiteState, getLoadedProductsAndDefaultPaymentTerms, OrderBuilderState } from '../../../../reducers';
import {
  LOAD_PAYMENT_TERMS_FROM_CONTRACT,
  LOAD_PAYMENT_TERMS_FROM_PRODUCT,
  LOAD_PAYMENT_TERMS_FROM_SALESAREAS,
  LOAD_PAYMENT_TERMS_OP_FROM_PRODUCT,
  PaymentTermsActions,
  PaymentTermsLoadFromContractAction,
  PaymentTermsLoadFromProductAction,
  PaymentTermsLoadFromProductCompleteAction,
  PaymentTermsLoadFromSalesAreasAction,
  PaymentTermsLoadFromSalesAreasCompleteAction,
  PaymentTermsResetAction,
} from '../../actions/payment-terms.actions';
import { LOAD_CONFIG_COMPLETE, SpecificationLoadConfigCompleteAction } from '../../actions/specification.actions';
import { getPaymentTermsRequestData } from '../../selectors/payment-terms.selector';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';

@Injectable()
export class PaymentTermsEffects {
  specificationConfigLoadComplete: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_CONFIG_COMPLETE),
      switchMap((payload: SpecificationLoadConfigCompleteAction) => {
        return this.store.select(getJobsiteState).pipe(
          take(1),
          map((state) => {
            return new PaymentTermsLoadFromSalesAreasAction(state.jobsiteSalesArea);
          }),
        );
      }),
    ),
  );

  paymentTermsLoad: Observable<PaymentTermsActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_PAYMENT_TERMS_FROM_SALESAREAS),
      map((action: PaymentTermsLoadFromSalesAreasAction) => this.getSalesAreasPaymentTermIds(action.payload)),
      filter((paymentTermId) => paymentTermId !== null),
      switchMap((paymentTermId) => {
        return this.paymentTermsService.getJobsitePaymentTermsByIdV2(paymentTermId).pipe(
          map((paymentTerm) => {
            if (paymentTerm) {
              return new PaymentTermsLoadFromSalesAreasCompleteAction(paymentTerm);
            }

            return new PaymentTermsLoadFromSalesAreasCompleteAction(null);
          }),
        );
      }),
    ),
  );

  paymentTermsLoadFromProduct: Observable<PaymentTermsActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_PAYMENT_TERMS_FROM_PRODUCT, LOAD_PAYMENT_TERMS_OP_FROM_PRODUCT),
      mergeMap((action: PaymentTermsLoadFromProductAction) => {
        return this.store
          .select(getPaymentTermsRequestData)
          .pipe(take(1))
          .pipe(
            switchMap((data) => {
              const payload: any = {
                countryCode: data.countryCode,
                productLineId: data.productLineId,
                jobsiteId: data.jobsiteId,
                customerId: data.customerId,
              };
              const productId = _safe(
                () => Number(action.payload.productId || action.payload.product.productId),
                undefined,
              );
              if (productId) {
                payload['productId'] = productId;
              }
              return this.paymentTermsService.getCustomerPaymentTermsV2(payload);
            }),
            map((response: any) => {
              return new PaymentTermsLoadFromProductCompleteAction({
                productId: _safe(() => action.payload.productId || action.payload.product.productId),
                paymentTerms: response.paymentTerms,
              });
            }),
          );
      }),
    ),
  );

  paymentTermsLoadFromContract: Observable<PaymentTermsActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_PAYMENT_TERMS_FROM_CONTRACT),
      switchMap((action: PaymentTermsLoadFromContractAction) => {
        return this.store
          .select(getLoadedProductsAndDefaultPaymentTerms)
          .pipe(take(1))
          .pipe(
            map((properties: { loadedProducts: IProduct[]; defaultPaymentTerm: PaymentTerm }) => {
              const selectedProduct = properties.loadedProducts.filter(
                (product) => product.product.productId === action.payload.productId,
              );
              return { defaultPaymentTerm: properties.defaultPaymentTerm, selectedProduct };
            }),
            map(({ defaultPaymentTerm, selectedProduct }) => {
              const paymentTermUrl =
                selectedProduct[0] &&
                selectedProduct[0].salesDocument &&
                selectedProduct[0].salesDocument.paymentTerm &&
                selectedProduct[0].salesDocument.paymentTerm.links &&
                selectedProduct[0].salesDocument.paymentTerm.links.self;

              return { defaultPaymentTerm: defaultPaymentTerm, paymentTermUrl };
            }),
            switchMap(({ defaultPaymentTerm, paymentTermUrl }) => {
              if (paymentTermUrl) {
                return this.paymentTermsService.getPaymentTermsByUrlInProduct(paymentTermUrl);
              }

              if (defaultPaymentTerm) {
                return of(defaultPaymentTerm);
              }

              return of([]);
            }),
            map((response) => {
              return new PaymentTermsLoadFromProductCompleteAction({
                productId: action.payload.productId,
                paymentTerms: Array.isArray(response) ? response : [response],
              });
            }),
          );
      }),
    ),
  );

  specificationsReset$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(RESET_BUILDER),
      map(() => new PaymentTermsResetAction()),
    ),
  );

  getSalesAreasPaymentTermIds(salesAreas: IJobsiteSalesArea[]) {
    if (salesAreas.length === 0) {
      return null;
    }

    if (salesAreas[0].paymentTerm && salesAreas[0].paymentTerm.paymentTermId) {
      return salesAreas[0].paymentTerm.paymentTermId;
    }

    return null;
  }

  constructor(
    private actions$: Actions,
    private store: Store<OrderBuilderState>,
    private paymentTermsService: PaymentTermService,
  ) {}
}
